import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './styles/index.css';
import 'react-quill/dist/quill.snow.css';
import {
  TextEditor,
  ImageUploader,
  VideoUploader,
  AudioUploader,
  LinkEditor,
  AttachmentEditor
} from '../../components/content-editors';
import BackgroundEditor from '../../components/content-editors/BackgroundEditor';
import DividerEditor from '../../components/content-editors/DividerEditor';
import {
  ProductBasicInfo,
  NutritionTable,
  UsageMethod,
  RelatedLinks,
  InspectionReport
} from '../../components/templates';
import GDSTraceabilityEditor from '../../components/templates/GDSTraceabilityEditor';
import { ensureGIContentItems, getGIModuleDefaults, giStorageType, findGIModule } from '../../components/gi-label/giContent';
import GILabelEditor from '../../components/gi-label/GILabelEditor';
import { isGIVariant } from '../../components/gi-label/giThemes';
import StylePanel from '../../components/common/StylePanel';
import './styles/style-toolbar.css';
import { Tag, User } from '@icon-park/react';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { createLabel, getQRCodeBaseUrl } from '../../api/labels';
import { uploadFile } from '../../api/files';
import { ENV_CONFIG, BUSINESS_CONFIG } from '../../config';
import { getGdsHeaderStyle, getGdsTheme } from '../../config/gdsThemes';
import { REQUIRED_NUTRIENTS, getStandardUnit } from '../../config/nutrition.config';
import { validateNutritionTable } from '../../utils/nutritionUtils';
import { getDefaultDataForType as getDefaultDataForTypeFromUtils } from './utils/contentDefaults';
import CreatorToolbar from './components/CreatorToolbar';
import ContentPanel from './components/ContentPanel';
import PreviewPanel from './components/PreviewPanel';
import MobileNav from './components/MobileNav';
import ShareModal from './components/ShareModal';
import ChangeReasonDialog from './components/ChangeReasonDialog';
import { labelNumber, changeReasonError } from '../../utils/labelTrace';
import { saveLabelUpdate } from '../../utils/labelUpdate';
// 草稿自动保存
import useAutoSave from '../../hooks/useAutoSave';
import DraftRecoveryModal from '../../components/common/DraftRecoveryModal';
import { generateDraftId, getDraft, deleteDraft, findDraftByLabelId, findRecentCreateDraft } from '../../services/draftService';

function LabelCreator({ onClose, username, initialTemplates = [], initialTemplateVariant = 'default', initialLabelData = null }) {
  console.log('🏗️ [LabelCreator] 组件初始化');
  console.log('📥 [LabelCreator] 接收到的props:');
  console.log('  - initialTemplates:', initialTemplates);
  console.log('  - initialTemplateVariant:', initialTemplateVariant);
  console.log('  - username:', username);
  console.log('  - initialLabelData:', initialLabelData);
  
  // 判断是否为编辑模式
  const isEditMode = !!initialLabelData;
  const editingLabelId = initialLabelData?.id || initialLabelData?.labelId || null;
  
  // 从初始数据加载状态（如果是编辑模式）
  const [labelName, setLabelName] = useState(initialLabelData?.labelName || '');
  const [backgroundImage, setBackgroundImage] = useState(initialLabelData?.backgroundImage || null);
  const [backgroundColor, setBackgroundColor] = useState(initialLabelData?.backgroundColor || '#ffffff');
  const [borderColor, setBorderColor] = useState(initialLabelData?.borderColor || '#000000');
  
  // 内容项列表
  const [contentItems, setContentItems] = useState([]);
  const [selectedContentId, setSelectedContentId] = useState(null);
  
  // 移动端视图切换
  const [mobileView, setMobileView] = useState('settings'); // 'settings', 'editor', 'preview'
  const [showPreview, setShowPreview] = useState(true);
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);
  const savedLabel = useRef(initialLabelData);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const traceNumber = labelNumber(initialLabelData);
  // 模板变体选择：'default' | 'blue-white' | 'brown-v1'
  const [templateVariant, setTemplateVariant] = useState(initialTemplateVariant);
  console.log('🎨 [LabelCreator] 初始化templateVariant state:', templateVariant);
  
  // 标签全局主题配置（用于渲染与提交）
  const [labelMeta, setLabelMeta] = useState(() => {
    console.log('⚙️ [LabelCreator] 初始化labelMeta');
    console.log('  - initialTemplateVariant:', initialTemplateVariant);
    console.log('  - initialLabelData?.meta:', initialLabelData?.meta);
    
    // 如果是编辑模式且有meta数据，优先使用已有数据
    if (initialLabelData?.meta) {
      console.log('✅ [LabelCreator] 使用已有标签的meta配置:', initialLabelData.meta);
      return initialLabelData.meta;
    }
    
    if (isGIVariant(initialTemplateVariant)) return { templateVariant: initialTemplateVariant, headerVisible: false, footerText: '', watermark: { enabled: false } };
    // 否则根据模板变体设置默认配置
    if (initialTemplateVariant === 'blue-white') {
      const meta = { templateVariant: 'blue-white', headerVisible: true, footerText: '', decorations: { enabled: false }, watermark: { enabled: true } };
      console.log('🔵 [LabelCreator] 使用blue-white配置:', meta);
      return meta;
    }
    if (initialTemplateVariant === 'brown-v1') {
      const meta = { templateVariant: 'brown-v1', headerVisible: false, footerText: '技术支持：数字标签平台', decorations: { enabled: true }, watermark: { enabled: true } };
      console.log('🟤 [LabelCreator] 使用brown-v1配置:', meta);
      return meta;
    }
    // GDS 风格配色模板：深色渐变页头 + 无装饰线（靠卡片分隔）
    const gdsHeaderStyle = getGdsHeaderStyle(initialTemplateVariant);
    if (gdsHeaderStyle) {
      const meta = {
        templateVariant: initialTemplateVariant,
        headerVisible: true,
        footerText: '技术支持：数字标签平台',
        decorations: { enabled: false },
        watermark: { enabled: true },
        headerStyle: gdsHeaderStyle,
        // 卡片之间留出呼吸感，贴合 GDS 设计稿的分块节奏
        layout: { blockGap: 14 }
      };
      console.log('🎨 [LabelCreator] 使用GDS主题配置:', meta);
      return meta;
    }
    const meta = { templateVariant: 'default', headerVisible: true, footerText: '', decorations: { enabled: false }, watermark: { enabled: true } };
    console.log('⚪ [LabelCreator] 使用default配置:', meta);
    return meta;
  });

  // 当initialTemplateVariant改变时，同步更新templateVariant与labelMeta.templateVariant
  useEffect(() => {
    const variant = initialLabelData?.meta?.templateVariant || initialTemplateVariant;
    setTemplateVariant(variant);
    setLabelMeta(m => ({ ...m, templateVariant: variant }));
  }, [initialTemplateVariant, initialLabelData?.meta?.templateVariant]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ type: '', message: '' });
  const [shareLink, setShareLink] = useState('');
  const [createdId, setCreatedId] = useState(null);
  const [qrcodeBaseUrl, setQrcodeBaseUrl] = useState(null); // 从后端获取的二维码基础URL

  // === 草稿自动保存相关状态 ===
  const [showDraftRecoveryModal, setShowDraftRecoveryModal] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftInitialized, setDraftInitialized] = useState(false);

  // 生成草稿ID
  const draftId = useMemo(() => {
    return generateDraftId(editingLabelId, initialTemplateVariant);
  }, [editingLabelId, initialTemplateVariant]);

  // 自动保存Hook
  const {
    status: autoSaveStatus,
    error: autoSaveError,
    updateData: updateAutoSaveData,
    saveNow: saveAutoSaveNow,
    clearDraft: clearAutoSaveDraft,
    getFormattedSaveTime,
  } = useAutoSave({
    enabled: true,
    draftId,
    onSave: (result) => {
      console.log('💾 [AutoSave] 草稿已保存:', result);
    },
    onError: (error) => {
      console.error('❌ [AutoSave] 保存失败:', error);
    },
  });

  // 检查是否有可恢复的草稿
  useEffect(() => {
    if (draftInitialized) return;

    // 编辑模式：检查是否有该标签的草稿
    if (isEditMode && editingLabelId) {
      const existingDraft = findDraftByLabelId(editingLabelId);
      if (existingDraft) {
        // 比较草稿时间和服务器数据时间，如果草稿更新则提示恢复
        const draftTime = new Date(existingDraft.savedAt).getTime();
        const serverTime = initialLabelData?.updatedAt
          ? new Date(initialLabelData.updatedAt).getTime()
          : 0;

        if (draftTime > serverTime) {
          setPendingDraft(existingDraft);
          setShowDraftRecoveryModal(true);
          setDraftInitialized(true);
          return;
        }
      }
    }
    // 创建模式：检查是否有最近的草稿
    else if (!isEditMode) {
      const recentDraft = findRecentCreateDraft(initialTemplateVariant);
      if (recentDraft) {
        // 只恢复最近30分钟内的草稿
        const draftTime = new Date(recentDraft.savedAt).getTime();
        const now = Date.now();
        const thirtyMinutes = 30 * 60 * 1000;

        if (now - draftTime < thirtyMinutes) {
          setPendingDraft(recentDraft);
          setShowDraftRecoveryModal(true);
          setDraftInitialized(true);
          return;
        }
      }
    }

    setDraftInitialized(true);
  }, [isEditMode, editingLabelId, initialTemplateVariant, initialLabelData, draftInitialized]);

  // 恢复草稿
  const handleRecoverDraft = useCallback(() => {
    if (!pendingDraft?.data) return;

    const data = pendingDraft.data;

    // 恢复标签名称
    if (data.labelName) {
      setLabelName(data.labelName);
    }

    // 恢复背景设置
    if (data.backgroundImage !== undefined) {
      setBackgroundImage(data.backgroundImage);
    }
    if (data.backgroundColor) {
      setBackgroundColor(data.backgroundColor);
    }
    if (data.borderColor) {
      setBorderColor(data.borderColor);
    }

    // 恢复模板变体
    if (data.templateVariant) {
      setTemplateVariant(data.templateVariant);
    }

    // 恢复标签元数据
    if (data.labelMeta) {
      setLabelMeta(data.labelMeta);
    }

    // 恢复内容项
    if (data.contentItems && data.contentItems.length > 0) {
      setContentItems(isGIVariant(data.labelMeta?.templateVariant || data.templateVariant) ? ensureGIContentItems(data.contentItems) : data.contentItems);
      setSelectedContentId(data.contentItems[0].id);
    }

    console.log('✅ [Draft] 草稿已恢复');
    setShowDraftRecoveryModal(false);
    setPendingDraft(null);
  }, [pendingDraft]);

  // 放弃草稿
  const handleDiscardDraft = useCallback(() => {
    if (pendingDraft?.id) {
      deleteDraft(pendingDraft.id);
      console.log('🗑️ [Draft] 草稿已放弃');
    }
    setShowDraftRecoveryModal(false);
    setPendingDraft(null);
  }, [pendingDraft]);

  // 当数据变化时，更新自动保存数据
  useEffect(() => {
    if (!draftInitialized) return;

    updateAutoSaveData({
      labelName,
      backgroundImage,
      backgroundColor,
      borderColor,
      templateVariant,
      labelMeta,
      contentItems,
      isEditMode,
      editingLabelId,
      initialTemplates,
    });
  }, [
    labelName,
    backgroundImage,
    backgroundColor,
    borderColor,
    templateVariant,
    labelMeta,
    contentItems,
    isEditMode,
    editingLabelId,
    initialTemplates,
    draftInitialized,
    updateAutoSaveData,
  ]);

  // 组件加载时从后端获取二维码基础URL
  useEffect(() => {
    const fetchQRCodeBaseUrl = async () => {
      try {
        const result = await getQRCodeBaseUrl();
        const baseUrl = result?.data || result || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
        setQrcodeBaseUrl(baseUrl);
      } catch (error) {
        console.warn('获取二维码基础URL失败，使用默认值:', error);
        setQrcodeBaseUrl(BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin);
      }
    };
    fetchQRCodeBaseUrl();
  }, []);

  // 拖拽传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 拖动8px后才激活拖拽，避免误触
      },
    })
  );

  // 处理拖拽结束
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setContentItems((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      // 使用 arrayMove 重新排序
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  // 检测是否应该使用标准食品标签模板
  // 首屏还未完成初始化时，使用 initialTemplates 作为回退，避免短暂显示为"非标准"
  const plannedTypes = (contentItems && contentItems.length > 0)
    ? contentItems.map(item => item.type)
    : (initialTemplates || []);
  const hasStandardTemplateComponents = [
    'image', 'product-basic', 'nutrition', 'usage', 'inspection', 'related-links'
  ].every(t => plannedTypes.includes(t));
  
  // 当模板变体改变时，自动启用相关组件的标题配置
  useEffect(() => {
    console.log('🔄 [LabelCreator] useEffect - 模板变体改变监听');
    console.log('🎨 [LabelCreator] templateVariant:', templateVariant);
    console.log('📋 [LabelCreator] hasStandardTemplateComponents:', hasStandardTemplateComponents);
    
    if (templateVariant === 'blue-white' && hasStandardTemplateComponents) {
      console.log('🔵 [LabelCreator] 检测到blue-white变体且为标准模板组件，开始启用标题配置');
      
      setContentItems(prevItems => {
        console.log('📋 [LabelCreator] 当前contentItems:', prevItems);
        
        const updatedItems = prevItems.map(item => {
          // 只对支持标题配置的组件启用
          if (['product-basic', 'nutrition', 'usage'].includes(item.type)) {
            console.log(`🔧 [LabelCreator] 处理组件 ${item.type}，检查标题配置`);
            
            const currentData = item.data || {};
            const currentTitleConfig = currentData.titleConfig || {};
            console.log(`📝 [LabelCreator] ${item.type}当前标题配置:`, currentTitleConfig);
            
            // 如果还没有启用，则启用并设置默认值
            if (!currentTitleConfig.enabled) {
              console.log(`✅ [LabelCreator] ${item.type}标题配置未启用，开始启用`);
              
              const defaultTitleConfigs = {
                'product-basic': {
                  enabled: true,
                  chinese: '产品基础信息',
                  english: 'Product Basic information.',
                  bgColor: '#1e90ff',
                  textColor: '#ffffff'
                },
                'nutrition': {
                  enabled: true,
                  chinese: '营养成分表',
                  english: 'Nutrition Information.',
                  bgColor: '#1e90ff',
                  textColor: '#ffffff'
                },
                'usage': {
                  enabled: true,
                  chinese: '食用方法',
                  english: 'Direction for Consumption.',
                  bgColor: '#1e90ff',
                  textColor: '#ffffff'
                }
              };
              
              const newTitleConfig = {
                ...defaultTitleConfigs[item.type],
                ...currentTitleConfig
              };
              console.log(`✅ [LabelCreator] ${item.type}新标题配置:`, newTitleConfig);
              
              const updatedItem = {
                ...item,
                data: {
                  ...currentData,
                  titleConfig: newTitleConfig
                }
              };
              console.log(`✅ [LabelCreator] ${item.type}更新后的内容项:`, updatedItem);
              return updatedItem;
            } else {
              console.log(`ℹ️ [LabelCreator] ${item.type}标题配置已启用，跳过`);
            }
          }
          return item;
        });
        
        console.log('📋 [LabelCreator] 更新后的contentItems:', updatedItems);
        return updatedItems;
      });
    } else {
      console.log('ℹ️ [LabelCreator] 不需要启用标题配置:', {
        templateVariant,
        hasStandardTemplateComponents,
        条件满足: templateVariant === 'blue-white' && hasStandardTemplateComponents
      });
    }
  }, [templateVariant, hasStandardTemplateComponents]);
  const [showShareModal, setShowShareModal] = useState(false);
  
  
  // 预览项的 refs
  const previewRefs = useRef({});

  // 初始化模板：如果传入了 initialTemplates 或 initialLabelData，自动添加这些模板
  useEffect(() => {
    console.log('🔄 [LabelCreator] useEffect - 初始化模板');
    console.log('📋 [LabelCreator] initialTemplates:', initialTemplates);
    console.log('🎨 [LabelCreator] initialTemplateVariant:', initialTemplateVariant);
    console.log('📝 [LabelCreator] initialLabelData:', initialLabelData);
    console.log('✏️ [LabelCreator] isEditMode:', isEditMode);
    
    // 如果是编辑模式，从 initialLabelData 加载内容项
    if (isEditMode && initialLabelData?.contentItems) {
      console.log('✅ [LabelCreator] 编辑模式：从initialLabelData加载内容项');
      let loadedItems = initialLabelData.contentItems.map((item, index) => ({
        id: item.itemId || Date.now() + index,
        type: item.contentType,
        data: typeof item.contentData === 'string' ? JSON.parse(item.contentData) : item.contentData,
      }));
      if (isGIVariant(initialLabelData.meta?.templateVariant)) loadedItems = ensureGIContentItems(loadedItems);
      console.log('📦 [LabelCreator] 加载的内容项:', loadedItems);
      setContentItems(loadedItems);
      if (loadedItems.length > 0) {
        setSelectedContentId(loadedItems[0].id);
      }
      return;
    }
    
    // 创建模式：如果传入了 initialTemplates，自动添加这些模板
    if (initialTemplates && initialTemplates.length > 0) {
      console.log('✅ [LabelCreator] 开始处理', initialTemplates.length, '个初始模板');
      
      const initialItems = initialTemplates.map((templateType, index) => {
        console.log(`📦 [LabelCreator] 处理第${index + 1}个模板:`, templateType);
        
        const defaultData = isGIVariant(initialTemplateVariant) ? getGIModuleDefaults(templateType) : getDefaultDataForType(templateType);
        if (templateType === 'product-core') return { id: Date.now() + index, type: giStorageType(templateType), data: defaultData };
        console.log(`📝 [LabelCreator] 模板${templateType}的默认数据:`, defaultData);
        
        // 根据模板变体调整背景组件的颜色
        if (templateType === 'background') {
          let backgroundColor = defaultData.color;
          
          if (initialTemplateVariant === 'blue-white') {
            // blue-white 模板使用白色背景
            backgroundColor = '#ffffff';
            console.log(`🔵 [LabelCreator] blue-white模板，设置背景为白色:`, backgroundColor);
          } else if (initialTemplateVariant === 'brown-v1') {
            // brown-v1 模板保持默认的浅棕色
            backgroundColor = '#F5E6D3';
            console.log(`🟤 [LabelCreator] brown-v1模板，使用默认浅棕色背景:`, backgroundColor);
          } else if (getGdsTheme(initialTemplateVariant)) {
            // GDS 主题使用各自的纸底色
            backgroundColor = getGdsTheme(initialTemplateVariant).paper;
            console.log(`🎨 [LabelCreator] GDS模板，设置主题底色:`, backgroundColor);
          } else {
            // 默认模板使用白色背景
            backgroundColor = '#ffffff';
            console.log(`⚪ [LabelCreator] 默认模板，设置背景为白色:`, backgroundColor);
          }
          
          const item = {
            id: Date.now() + index,
            type: templateType,
            data: {
              ...defaultData,
              color: backgroundColor
            }
          };
          console.log(`✅ [LabelCreator] 创建背景组件:`, item);
          return item;
        }
        
        // 使用initialTemplateVariant而不是templateVariant（因为useEffect执行时templateVariant可能还没更新）
        // 如果模板变体是blue-white，且组件支持标题配置，自动启用标题配置
        if (initialTemplateVariant === 'blue-white' && ['product-basic', 'nutrition', 'usage'].includes(templateType)) {
          console.log(`🔵 [LabelCreator] 为${templateType}启用blue-white标题配置`);
          
          const defaultTitleConfigs = {
            'product-basic': {
              enabled: true,
              chinese: '产品基础信息',
              english: 'Product Basic information.',
              bgColor: '#1e90ff',
              textColor: '#ffffff'
            },
            'nutrition': {
              enabled: true,
              chinese: '营养成分表',
              english: 'Nutrition Information.',
              bgColor: '#1e90ff',
              textColor: '#ffffff'
            },
            'usage': {
              enabled: true,
              chinese: '食用方法',
              english: 'Direction for Consumption.',
              bgColor: '#1e90ff',
              textColor: '#ffffff'
            }
          };
          
          const titleConfig = {
            ...defaultTitleConfigs[templateType], // 先使用 blue-white 配置，确保 enabled: true
            ...(defaultData.titleConfig || {}), // 然后用默认配置的其他属性覆盖（但不会覆盖 enabled）
            enabled: true, // 强制设置为 true，确保标题显示
          };
          console.log(`✅ [LabelCreator] ${templateType}的标题配置:`, titleConfig);
          
          const item = {
            id: Date.now() + index,
            type: templateType,
            data: {
              ...defaultData,
              titleConfig: titleConfig
            }
          };
          console.log(`✅ [LabelCreator] 创建带标题配置的内容项:`, item);
          return item;
        }
        
        const item = {
          id: Date.now() + index,
          type: templateType,
          data: defaultData
        };
        console.log(`✅ [LabelCreator] 创建普通内容项:`, item);
        return item;
      });
      
      console.log('📋 [LabelCreator] 最终生成的内容项列表:', initialItems);
      console.log('📊 [LabelCreator] 内容项统计:', {
        总数: initialItems.length,
        类型分布: initialItems.reduce((acc, item) => {
          acc[item.type] = (acc[item.type] || 0) + 1;
          return acc;
        }, {})
      });
      
      setContentItems(initialItems);
      console.log('✅ [LabelCreator] 已设置contentItems state');
      
      // 选中第一个模板
      if (initialItems.length > 0) {
        const firstId = initialItems[0].id;
        console.log('🎯 [LabelCreator] 选中第一个内容项，ID:', firstId, '类型:', initialItems[0].type);
        setSelectedContentId(firstId);
      }
    } else {
      console.log('⚠️ [LabelCreator] 没有初始模板，contentItems保持为空数组');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]); // 当编辑模式改变时重新执行

  // 当选中内容改变时，滚动到对应预览项（仅在预览面板内滚动，不影响整个页面）
  useEffect(() => {
    if (selectedContentId && previewRefs.current[selectedContentId]) {
      // 使用 setTimeout 确保 DOM 已更新，避免动画冲突
      const timer = setTimeout(() => {
        const element = previewRefs.current[selectedContentId];
        if (element) {
          // 查找最近的滚动容器（预览面板）
          const previewPanel = element.closest('.preview-panel');
          if (previewPanel) {
            // 在预览面板内滚动
            const elementTop = element.offsetTop;
            const elementHeight = element.offsetHeight;
            const panelHeight = previewPanel.clientHeight;
            const scrollTop = elementTop - (panelHeight / 2) + (elementHeight / 2);
            previewPanel.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            });
          } else {
            // 如果没有找到预览面板，使用默认行为但限制在最近的滚动容器内
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }
      }, 100); // 延迟执行，等待动画完成
      
      return () => clearTimeout(timer);
    }
  }, [selectedContentId]);

  // 防止预览面板滚动事件冒泡到页面级别
  useEffect(() => {
    const handlePreviewWheel = (e) => {
      // 如果滚动发生在预览面板内，阻止事件冒泡到页面级别
      const previewPanel = e.currentTarget;
      const isAtTop = previewPanel.scrollTop <= 0;
      const isAtBottom = previewPanel.scrollTop + previewPanel.clientHeight >= previewPanel.scrollHeight - 1;
      
      // 如果预览面板可以继续滚动，阻止冒泡
      if (!isAtTop || !isAtBottom) {
        // 面板内部还有滚动空间，阻止事件冒泡
        e.stopPropagation();
      } else {
        // 在边界时，根据滚动方向决定是否冒泡
        // 如果向上滚动且已到达顶部，允许冒泡（让页面滚动）
        // 如果向下滚动且已到达底部，允许冒泡（让页面滚动）
        // 否则阻止冒泡
        if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
          // 尝试向边界外滚动，允许冒泡
          return;
        } else {
          // 向边界内滚动，阻止冒泡
          e.stopPropagation();
        }
      }
    };

    // 查找预览面板元素（只在预览可见时才绑定事件）
    if (showPreview || mobileView === 'preview') {
      const previewPanel = document.querySelector('.preview-panel');
      if (previewPanel) {
        previewPanel.addEventListener('wheel', handlePreviewWheel, { passive: false });
        
        return () => {
          previewPanel.removeEventListener('wheel', handlePreviewWheel);
        };
      }
    }
  }, [showPreview, mobileView]); // 使用 showPreview 和 mobileView 作为依赖

  // 样式缓存：从 localStorage 加载，删除组件时保存样式，添加时恢复
  const STYLE_CACHE_KEY = 'label-creator-style-cache';
  const [styleCache, setStyleCache] = useState(() => {
    try {
      const cached = localStorage.getItem(STYLE_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  // 样式缓存变化时持久化到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STYLE_CACHE_KEY, JSON.stringify(styleCache));
    } catch (e) {
      console.warn('保存样式缓存失败:', e);
    }
  }, [styleCache]);

  // 添加内容项
  const handleAddContent = (type) => {
    const cachedStyle = styleCache[type];
    const newItem = {
      id: Date.now(),
      type: giStorageType(type),
      data: cachedStyle ? { ...getDefaultDataForType(type), containerStyle: cachedStyle } : getDefaultDataForType(type)
    };
    setContentItems(prevItems => [...prevItems, newItem]);
    setSelectedContentId(newItem.id);
  };

  // 获取不同类型的默认数据
  const getDefaultDataForType = (type) => {
    // 先尝试从工具函数获取
    const defaultData = getDefaultDataForTypeFromUtils(type);
    if (defaultData && Object.keys(defaultData).length > 0) {
      return defaultData;
    }
    
    // 兼容旧代码（如果工具函数没有返回，使用switch）
    switch (type) {
      // 基础内容
      case 'text':
        return { title: '', content: '', titleColor: '', contentColor: '', backgroundColor: '#ffffff' };
      case 'image':
        return { images: [], backgroundColor: '#ffffff', description: '' };
      case 'video':
        return { video: null };
      case 'audio':
        return { audio: null };
      case 'link':
        return { links: [{ icon: '', name: '官方网站', url: '', order: 1 }] };
      case 'attachment':
        return { attachments: [
          { name: '标定文本', file: null },
          { name: '检验报告', file: null }
        ] };
      // 业务模板
      case 'product-basic':
        return { 
          productName: '', ingredients: '', netContent: '', standardCode: '',
          manufacturer: '', address: '', origin: '', productionDate: '',
          shelfLife: '', storageConditions: '', productionLicense: '', serviceHotline: '',
          customFields: []
        };
      case 'nutrition':
        // 使用营养配置模块生成默认列表
        return { 
          items: REQUIRED_NUTRIENTS.map(name => ({
            name,
            per100g: '0',
            unit: getStandardUnit(name),
            nrv: '%',
            locked: false
          }))
        };
      case 'usage':
        return { title: '', video: null, backgroundColor: '#1e90ff' };
      case 'related-links':
        return { links: [{ enabled: true, icon: '', name: '官方网站', url: '', order: 1 }] };
      case 'inspection':
        return { 
          borderColor: '#cccccc',
          attachments: [
            { enabled: true, name: '标准文本', file: null },
            { enabled: true, name: '检验报告', file: null }
          ]
        };
      case 'gds-traceability':
        return {
          productName: '产品名称',
          productCode: '',
          productionDate: '',
          carouselImages: [],
          barcodeValue: '',
          tabs: [
            {
              label: '产品信息',
              icon: '📦',
              fields: [
                { label: '产品名称', value: '' },
                { label: '规格型号', value: '' },
                { label: '保质期', value: '' },
                { label: '储存条件', value: '' }
              ]
            },
            {
              label: '生产信息',
              icon: '🏭',
              fields: [
                { label: '生产企业', value: '' },
                { label: '生产地址', value: '' },
                { label: '生产日期', value: '' },
                { label: '生产批次', value: '' }
              ]
            },
            {
              label: '质检信息',
              icon: '✅',
              fields: [
                { label: '检验报告', value: '' },
                { label: '检验日期', value: '' },
                { label: '检验机构', value: '' },
                { label: '检验结果', value: '' }
              ]
            }
          ],
          certifications: []
        };
      default:
        return {};
    }
  };

  // 删除内容项
  const handleDeleteContent = (id) => {
    setContentItems(prevItems => {
      const target = prevItems.find(item => item.id === id);
      if (target?.type && target?.data?.containerStyle) {
        setStyleCache(cache => ({ ...cache, [target.type]: target.data.containerStyle }));
      }
      const newItems = prevItems.filter(item => item.id !== id);
      if (selectedContentId === id) {
        setSelectedContentId(newItems.length > 0 ? newItems[0].id : null);
      }
      return newItems;
    });
  };

  // 更新内容项数据
  const handleUpdateContent = (id, newData) => {
    setContentItems(prevItems => 
      prevItems.map(item => 
        item.id === id ? { ...item, data: newData } : item
      )
    );
  };

  // 上传背景图片
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 文件类型验证：只允许图片
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSubmitFeedback({ type: 'error', message: '只支持图片格式：JPG、PNG、GIF、WEBP' });
      return;
    }

    // 文件大小限制：5MB
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setSubmitFeedback({ type: 'error', message: '图片大小不能超过 5MB' });
      return;
    }

    try {
      setIsUploadingImage(true);
      setSubmitFeedback({ type: '', message: '' });

      // 上传文件到服务器，获取文件 URL
      const result = await uploadFile(file);
      console.log('上传结果:', result);
      
      // 根据后端返回的数据结构获取 fileUrl
      // client.js 返回的是 payload.data（即 LabelFile 对象）
      // 所以 result 应该是 LabelFile 对象，直接访问 fileUrl 属性
      const fileUrl = result?.fileUrl || result?.url || result?.data?.fileUrl || result?.data?.url;
      
      console.log('提取的 fileUrl:', fileUrl);
      console.log('result 对象:', JSON.stringify(result, null, 2));

      if (fileUrl) {
        // 保存 URL（而不是 Base64），URL 通常很短，完全在数据库字段限制内
        setBackgroundImage(fileUrl);
        console.log('背景图片已设置为:', fileUrl);
        setSubmitFeedback({ type: 'success', message: '图片上传成功' });
      } else {
        console.error('无法从结果中提取 fileUrl，完整结果:', result);
        throw new Error('上传失败：未获取到文件URL');
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      const errorMessage = error.message || '图片上传失败，请稍后再试';
      setSubmitFeedback({ type: 'error', message: errorMessage });
    } finally {
      setIsUploadingImage(false);
      // 清空 input，允许重复选择同一文件
      e.target.value = '';
    }
  };

  // 删除背景图片
  const handleDeleteBackground = () => {
    setBackgroundImage(null);
  };

  // 提交
  const handleSubmit = async (reason) => {
    if (submitLock.current) return;
    if (!labelName.trim()) {
      setSubmitFeedback({ type: 'error', message: '请填写标签名称' });
      return;
    }

    // 验证营养成分表数据
    const nutritionItems = contentItems.filter(item => item.type === 'nutrition');
    if (nutritionItems.length > 0) {
      for (const nutritionItem of nutritionItems) {
        const validation = validateNutritionTable(nutritionItem.data);
        
        if (!validation.valid) {
          // 如果有错误，阻止提交并显示错误信息
          const errorMsg = validation.errors.join('；');
          setSubmitFeedback({ 
            type: 'error', 
            message: `营养成分数据验证失败：${errorMsg}` 
          });
          return;
        }
        
        // 如果有警告，显示警告信息（但不阻止提交）
        if (validation.warnings.length > 0) {
          const warningMsg = validation.warnings.join('；');
          console.warn('营养成分数据警告：', warningMsg);
          // 可以选择性地显示警告给用户
          // setSubmitFeedback({ 
          //   type: 'warning', 
          //   message: `提示：${warningMsg}` 
          // });
        }
      }
    }

    if (isEditMode && editingLabelId && typeof reason !== 'string') {
      setChangeReason(''); setReasonError(''); setReasonOpen(true); return;
    }
    if (isEditMode && changeReasonError(reason)) {
      setReasonError(changeReasonError(reason)); return;
    }
    const payload = {
      labelName: labelName.trim(),
      backgroundImage,
      backgroundColor,
      borderColor,
      templateId: null,
      meta: labelMeta, // 保存标签的元数据（headerVisible, footerText, decorations等）
      contentItems: contentItems.map((item, index) => ({
        itemId: item.id,
        contentType: item.type,
        contentData: item.data,
        sortOrder: index + 1,
      })),
    };

    setSubmitFeedback({ type: '', message: '' });
    setReasonError('');
    submitLock.current = true;
    setIsSubmitting(true);
    try {
      let result;
      let labelId;
      
      // 判断是创建还是更新
      if (isEditMode && editingLabelId) {
        // 更新模式
        console.log('✏️ [LabelCreator] 更新标签，ID:', editingLabelId);
        const updated = await saveLabelUpdate(editingLabelId, payload, savedLabel.current, reason);
        result = updated.result;
        payload.meta = updated.meta;
        savedLabel.current = updated.saved;
        setLabelMeta(updated.meta);
        setReasonOpen(false); setChangeReason('');
        labelId = editingLabelId;
        setSubmitFeedback({ type: 'success', message: '标签更新成功' });
      } else {
        // 创建模式
        console.log('➕ [LabelCreator] 创建新标签');
        result = await createLabel(payload);
        labelId = result?.id || result?.labelId || result?.data?.id || result?.data?.labelId;
        setSubmitFeedback({ type: 'success', message: '标签创建成功' });
      }
      
      // 使用后端配置的二维码基础URL（如果没有获取到，则使用前端配置或当前域名作为回退）
      const base = qrcodeBaseUrl || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
      if (labelId) {
        setCreatedId(labelId);
        let url = `${base}/view/${labelId}`;

        // Mock模式下，为支持跨设备预览，将数据直接编码到URL参数
        if (ENV_CONFIG?.USE_MOCK) {
          const dataForShare = {
            id: labelId,
            ...payload,
          };
          try {
            const json = JSON.stringify(dataForShare);
            const encoded = btoa(unescape(encodeURIComponent(json)));
            const qp = `mock=1&d=${encoded}`;
            url = `${base}/view/${labelId}?${qp}`;
          } catch (e) {
            console.warn('编码分享数据失败，使用本地ID预览', e);
          }
        }
        setShareLink(url);
        setShowShareModal(true);

        // 提交成功后清除草稿
        clearAutoSaveDraft();

        // 如果是创建模式，清空表单
        if (!isEditMode) {
          setContentItems([]);
          setSelectedContentId(null);
          setLabelName('');
          setBackgroundImage(null);
        }
      } else {
        setShowShareModal(true);
      }
      // 成功后保留页面以便复制链接
    } catch (error) {
      const errorMessage = isEditMode 
        ? (error.message || '标签更新失败，请稍后再试')
        : (error.message || '标签创建失败，请稍后再试');
      setSubmitFeedback({ type: 'error', message: errorMessage });
      if (isEditMode) setReasonError(errorMessage);
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  // 更新容器样式（支持标题配置）
  const handleUpdateContainerStyle = (styleUpdate) => {
    if (!selectedContentId) return;
    setContentItems(prevItems => prevItems.map(item => {
      if (item.id !== selectedContentId) return item;
      // 如果传入的是containerStyle对象，直接更新；如果包含titleConfig，则合并更新
      const newData = { ...(item.data || {}) };
      // 使用 'in' 检查，支持传入 undefined 来清除样式
      if ('containerStyle' in styleUpdate) {
        newData.containerStyle = styleUpdate.containerStyle;
      }
      if ('titleConfig' in styleUpdate) {
        newData.titleConfig = styleUpdate.titleConfig;
      }
      // 兼容旧代码：如果直接传入containerStyle对象
      if (styleUpdate.enabled !== undefined || styleUpdate.bgColor !== undefined) {
        newData.containerStyle = styleUpdate;
      }
      return { ...item, data: newData };
    }));
  };

  const copyStyleToSameType = (style) => {
    if (!selectedContentId) return;
    const current = contentItems.find(i => i.id === selectedContentId);
    if (!current) return;
    setContentItems(prev => prev.map(it => {
      if (it.type !== current.type) return it;
      return { ...it, data: { ...(it.data || {}), containerStyle: { ...style, enabled: true } } };
    }));
  };

  const copyStyleToAll = (style) => {
    setContentItems(prev => prev.map(it => ({
      ...it,
      data: { ...(it.data || {}), containerStyle: { ...style, enabled: true } }
    })));
  };

  // 渲染右侧编辑器
  const renderEditor = () => {
    if (!selectedContentId) return null;
    
    const selectedItem = contentItems.find(item => item.id === selectedContentId);
    if (!selectedItem) return null;

    const props = {
      data: { ...selectedItem.data },  // 浅拷贝数据，避免直接引用
      onUpdate: (newData) => handleUpdateContent(selectedItem.id, newData)  // 使用 selectedItem.id 而不是 selectedContentId
    };

    const editorBody = (() => {
      switch (selectedItem.type) {
      // 基础内容
      case 'text':
        return <TextEditor {...props} />;
      case 'image':
        return <ImageUploader {...props} />;
      case 'video':
        return <VideoUploader {...props} />;
      case 'audio':
        return <AudioUploader {...props} />;
      case 'link':
        return <LinkEditor {...props} />;
      case 'attachment':
        return <AttachmentEditor {...props} />;
      case 'divider':
        return <DividerEditor {...props} />;
      case 'background':
        return <BackgroundEditor {...props} />;
      // 业务模板
      case 'product-basic':
        return <ProductBasicInfo {...props} />;
      case 'product-core':
        return <ProductBasicInfo {...props} data={{ ...props.data, moduleRole: 'product-core' }} />;
      case 'gi-product':
        return <GILabelEditor number={traceNumber} {...props} variant={labelMeta.templateVariant} onOpenModule={type => setSelectedContentId(findGIModule(contentItems, type)?.id)} />;
      case 'nutrition':
        return (
          <NutritionTable
            {...props}
            onOpenStyle={() => setShowStylePanel(true)}
            onCopyStyleSame={() => {
              const cur = (selectedItem.data && selectedItem.data.containerStyle) || {};
              copyStyleToSameType(cur);
            }}
            onCopyStyleAll={() => {
              const cur = (selectedItem.data && selectedItem.data.containerStyle) || {};
              copyStyleToAll(cur);
            }}
          />
        );
      case 'usage':
        return <UsageMethod {...props} />;
      case 'related-links':
        return <RelatedLinks {...props} />;
      case 'inspection':
        return <InspectionReport {...props} labelId={editingLabelId} giCertificate={isGIVariant(labelMeta.templateVariant)} />;
      case 'gds-traceability':
        return <GDSTraceabilityEditor {...props} />;
      default:
        return null;
    } })();

    // 顶部切换条：内容 / 样式
    return (
      <div>
        <div className="editor-style-toolbar">
          <button
            className={`est-btn ${!showStylePanel ? 'active' : ''}`}
            onClick={() => setShowStylePanel(false)}
          >内容</button>
          <button
            className={`est-btn ${showStylePanel ? 'active' : ''}`}
            onClick={() => setShowStylePanel(true)}
            title="设置该内容块的背景、边距、边框、阴影等"
          >样式</button>
        </div>
        {showStylePanel ? (
          <StylePanel
            value={{
              ...(selectedItem.data?.containerStyle || {}),
              titleConfig: selectedItem.data?.titleConfig || {}
            }}
            onChange={(update) => {
              // 分离containerStyle和titleConfig的更新
              const containerStyleUpdate = { ...update };
              const titleConfigUpdate = update.titleConfig;
              delete containerStyleUpdate.titleConfig;

              handleUpdateContainerStyle({
                containerStyle: Object.keys(containerStyleUpdate).length > 0 ? containerStyleUpdate : undefined,
                titleConfig: titleConfigUpdate
              });
            }}
            onCopyToSameType={copyStyleToSameType}
            onCopyToAll={copyStyleToAll}
            onResetToTemplateDefault={() => {
              // 清除该类型的样式缓存
              setStyleCache(cache => {
                const newCache = { ...cache };
                delete newCache[selectedItem.type];
                return newCache;
              });
              // 恢复到模板默认样式（清除containerStyle和titleConfig）
              handleUpdateContainerStyle({
                containerStyle: undefined,
                titleConfig: undefined
              });
            }}
            showTitleConfig={['product-basic', 'nutrition', 'usage', 'image'].includes(selectedItem.type)}
          />
        ) : null}
        {!showStylePanel ? editorBody : null}
      </div>
    );
  };


  // 根据模板变体选择模板类型（GDS 系列沿用标准食品标签布局，仅换配色皮肤）
  const templateType = hasStandardTemplateComponents 
    ? (templateVariant === 'blue-white' ? 'blue-white-template' : 'standard-food-label')
    : undefined;
  
  // 调试日志（开发时使用）
  if (process.env.NODE_ENV === 'development') {
    console.log('LabelCreator - Template Variant:', templateVariant);
    console.log('LabelCreator - Template Type:', templateType);
    console.log('LabelCreator - Has Standard Components:', hasStandardTemplateComponents);
  }

  const layoutClassNames = ['creator-main', showPreview ? '' : 'preview-hidden']
    .filter(Boolean)
    .join(' ');
  const shouldRenderPreview = showPreview || mobileView === 'preview';

  return (
    <div className={`label-creator-page ${isGIVariant(labelMeta.templateVariant) ? 'gi-creator' : ''}`}>
      {/* 顶部导航栏 */}
      <header className="creator-header">
        <div className="creator-header-left">
          <div className="creator-logo">
            <span className="creator-logo-icon">
              <Tag theme="outline" size={20} fill="#333" strokeWidth={3} />
            </span>
            <span className="creator-logo-text">食品数字标签生成系统</span>
          </div>
        </div>
        <div className="creator-header-right">
          <span className="creator-username">
            <User theme="outline" size={18} fill="#333" strokeWidth={3} /> {username || 'xiaoleixiao'}
          </span>
        </div>
      </header>

      {/* 操作区 */}
      <CreatorToolbar
        labelName={labelName}
        onLabelNameChange={setLabelName}
        onSubmit={handleSubmit}
        onClose={onClose}
        isSubmitting={isSubmitting}
        isEditMode={isEditMode}
        submitFeedback={submitFeedback}
        onOpenShare={() => setShowShareModal(true)}
        shareLink={shareLink}
        // 自动保存状态
        autoSaveStatus={autoSaveStatus}
        autoSaveTime={getFormattedSaveTime()}
        autoSaveError={autoSaveError}
        onManualSave={saveAutoSaveNow}
      />

      {/* 主内容区 */}
      <div className={layoutClassNames}>
        {/* 左侧操作台 */}
        <div className={`left-panel ${mobileView === 'settings' ? 'mobile-active' : 'mobile-hidden'}`}>
          <ContentPanel
            onGIThemeChange={variant => { setTemplateVariant(variant); setLabelMeta(m => ({ ...m, templateVariant: variant })); }}
            labelMeta={labelMeta}
            setLabelMeta={setLabelMeta}
            contentItems={contentItems}
            selectedContentId={selectedContentId}
            onSelectContent={setSelectedContentId}
            onDeleteContent={handleDeleteContent}
            onAddContent={handleAddContent}
            sensors={sensors}
            onDragEnd={handleDragEnd}
          />
        </div>

        {/* 右侧编辑区 */}
        <div className={`right-panel ${mobileView === 'editor' ? 'mobile-active' : 'mobile-hidden'}`}>
          <div key={selectedContentId}>
            {renderEditor()}
          </div>
        </div>

        <PreviewPanel
          identity={initialLabelData}
          visible={shouldRenderPreview}
          mobileView={mobileView}
          labelName={labelName}
          backgroundImage={backgroundImage}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          labelMeta={labelMeta}
          contentItems={contentItems}
        />
      </div>

      {/* 移动端底部导航 */}
      <MobileNav
        mobileView={mobileView}
        onChange={setMobileView}
        onPreview={() => {
          setShowPreview(true);
          setMobileView('preview');
        }}
      />

      <ChangeReasonDialog open={reasonOpen} reason={changeReason} onChange={setChangeReason}
        number={traceNumber} busy={isSubmitting} error={reasonError}
        onCancel={() => { if (!isSubmitting) setReasonOpen(false); }}
        onSave={() => handleSubmit(changeReason)} />
      {/* 分享弹窗（保存成功后弹出） */}
      <ShareModal
        isOpen={showShareModal}
        shareLink={shareLink}
        createdId={createdId}
        onClose={() => setShowShareModal(false)}
      />

      {/* 草稿恢复弹窗 */}
      <DraftRecoveryModal
        isOpen={showDraftRecoveryModal}
        draft={pendingDraft}
        onRecover={handleRecoverDraft}
        onDiscard={handleDiscardDraft}
        onClose={() => setShowDraftRecoveryModal(false)}
      />
    </div>
  );
}

export default LabelCreator;
